'use client'

import { useKeenSlider } from 'keen-slider/react'
import 'keen-slider/keen-slider.min.css'
import { useEffect, useRef, useState } from 'react'
import { getBaseImageUrl } from '@/constants/hooks'
import { EventBannerResponse, PromotionBannerResponse, SubPromotionBanner } from '@/services/dataTypes'
import { faAngleLeft, faAngleRight, FontAwesomeIcon } from '@/constants/icons'
import Image from 'next/image'

interface Props {
    sliders: EventBannerResponse[] | PromotionBannerResponse[] | SubPromotionBanner[];
    domain: string;
    eventName: string;
}

const EventSlider = ({ sliders, domain, eventName }: Props) => {
    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        loop: true,
        slides: {
            perView: 1,
        },
    })

    const [imgLoaded, setImgLoaded] = useState(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        if (!slider.current || sliders.length <= 1) return

        const startAutoSlide = () => {
            timeoutRef.current = setInterval(() => {
                slider.current?.next()
            }, 4000)
        }

        const stopAutoSlide = () => {
            if (timeoutRef.current) clearInterval(timeoutRef.current)
        }

        startAutoSlide()
        return () => stopAutoSlide()
    }, [slider, sliders.length])

    const BannerImage = ({ src, alt, loading, priority = false }: { src: string; alt: string; loading?: 'eager' | 'lazy'; priority?: boolean }) => (
        <div className="relative w-full overflow-hidden aspect-[21/9] md:aspect-[25/7]">
            {!imgLoaded && (
                <div className="absolute inset-0 animate-pulse bg-gray-200" />
            )}
            <Image
                src={getBaseImageUrl(domain, src, "")}
                fill
                style={{ objectFit: 'cover' }}
                alt={alt}
                onLoad={() => setImgLoaded(true)}
                className={`duration-700 ease-in-out ${imgLoaded ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
                priority={priority}
                fetchPriority={priority ? "high" : "auto"}
                loading={loading}
            />
        </div>
    )

    if (sliders?.length <= 1) {
        const slide = sliders[0]
        return (
            <div className="relative w-full rounded-2xl overflow-hidden shadow-sm border border-gray-100">
                {slide && (
                    <BannerImage
                        src={slide?.banner}
                        alt={eventName + "Banner"}
                        priority
                        loading="eager"
                    />
                )}
            </div>
        )
    }

    return (
        <div
            className="group relative w-full rounded-2xl overflow-hidden shadow-md border border-gray-100"
            onMouseEnter={() => timeoutRef.current && clearInterval(timeoutRef.current)}
            onMouseLeave={() => {
                timeoutRef.current = setInterval(() => {
                    slider.current?.next()
                }, 4000)
            }}
        >
            <div ref={sliderRef} className="keen-slider">
                {sliders.map((slide, i) => (
                    <div className="keen-slider__slide" key={i}>
                        <BannerImage
                            src={slide?.banner}
                            alt={`${eventName} Banner ${i + 1}`}
                            priority={i === 0}
                            loading={i === 0 ? 'eager' : 'lazy'}
                        />
                    </div>
                ))}
            </div>

            {/* Modern Floating Navigation Arrows */}
            <button
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/70 backdrop-blur-md text-gray-800 shadow-sm border border-white/50 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 active:scale-95"
                onClick={(e) => {
                    e.stopPropagation();
                    slider.current?.prev();
                }}
            >
                <FontAwesomeIcon icon={faAngleLeft} className="w-4 h-4" />
            </button>

            <button
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/70 backdrop-blur-md text-gray-800 shadow-sm border border-white/50 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 active:scale-95"
                onClick={(e) => {
                    e.stopPropagation();
                    slider.current?.next();
                }}
            >
                <FontAwesomeIcon icon={faAngleRight} className="w-4 h-4" />
            </button>

            {/* Bottom Progress/Indicator Dots (Optional addition) */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {sliders.map((_, idx) => (
                    <div 
                        key={idx} 
                        className="h-1.5 w-1.5 rounded-full bg-white/50 backdrop-blur-sm"
                    />
                ))}
            </div>
        </div>
    )
}

export default EventSlider